import React, { useState } from "react";
import { Info } from "lucide-react";
import { Button, Modal } from "../../../components/ui";
import type { DishImportResult } from "../types/dishes.types";

/** What the zoom control offers. 100% is where the grid opens. */
const ZOOM_LEVELS = [50, 75, 100, 125, 150] as const;

interface StatCardProps {
  label: string;
  value: number;
  /** `plain` is the tally; the other two are a verdict, and are coloured. */
  tone: "plain" | "success" | "error";
}

const CARD_TONES: Record<StatCardProps["tone"], { box: string; label: string }> = {
  plain: { box: "border-line bg-[#FAFBFC]", label: "text-ink-muted" },
  success: { box: "border-[#A6E4C1] bg-[#F4FDF7]", label: "text-[#2F8E5B]" },
  error: { box: "border-[#F5B5AE] bg-[#FFF6F5]", label: "text-[#D92D20]" },
};

/**
 * One of the three figures the report is headed with.
 *
 * The number is the size it is because it is the answer: a reader opening this
 * wants to know how bad it is before they read a single remark, and three
 * numbers at this size settle that in a glance. The label above stays small
 * and takes the colour, so the tint does the sorting rather than three
 * differently-coloured numerals shouting at each other.
 */
const StatCard: React.FC<StatCardProps> = ({ label, value, tone }) => {
  const styles = CARD_TONES[tone];
  return (
    <div className={`flex-1 rounded-[6px] border px-4 py-3.5 ${styles.box}`}>
      <p className={`text-[13px] ${styles.label}`}>{label}</p>
      <p className="mt-1 text-[26px] 3xl:text-[28px] font-medium leading-tight text-ink tabular-nums">
        {value}
      </p>
    </div>
  );
};

interface DishImportReportModalProps {
  open: boolean;
  /** The workbook that was sent — the dialog is titled with it. */
  fileName: string;
  result: DishImportResult | null;
  onClose: () => void;
  /** Saves the refused rows as a workbook. Throws with why, if it cannot. */
  onDownloadErrors: () => void;
}

/**
 * What the upload did, row by row.
 *
 * Drawn as a spreadsheet on purpose — the numbered gutter, the sheet tab, the
 * zoom — because the thing being reported on is a spreadsheet, and the row
 * numbers down the left are the reader's own: row 7 here is row 7 in the file
 * still open on their other screen. That is the whole trick of this screen. A
 * list of prose errors would be the same information and would not tell them
 * where to go.
 *
 * The remarks are the server's words, unedited. What is wrong with a row is
 * something only the importer knows — a base unit that is not gm or ml, a
 * portions cell in the wrong form — and rewording it here would put this file
 * in the business of tracking rules it does not own.
 */
const DishImportReportModal: React.FC<DishImportReportModalProps> = ({
  open,
  fileName,
  result,
  onClose,
  onDownloadErrors,
}) => {
  const [zoom, setZoom] = useState(100);
  const [downloadError, setDownloadError] = useState("");

  const handleDownload = () => {
    setDownloadError("");
    try {
      onDownloadErrors();
    } catch (cause) {
      setDownloadError(
        cause instanceof Error
          ? cause.message
          : "That file could not be saved. Try uploading the sheet again."
      );
    }
  };

  if (!result) return null;

  return (
    <Modal
      open={open}
      // The file's own name, which is what the reader called it and the only
      // heading that could tell two of these apart.
      title={fileName}
      onClose={onClose}
      className="max-w-[1180px]"
      footer={
        <>
          {/* Pushed left out of the button row: it is the one instruction on
              the screen, and it belongs beside the grid it is about rather
              than tucked against the buttons. */}
          <span className="mr-auto flex items-center gap-1.5 text-[13px] text-ink-muted">
            <Info className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            {downloadError || "Check rows with errors before re-uploading"}
          </span>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          {/* Only where there is a workbook to save. A sheet that landed whole
              has no refused rows to hand back, and the button would download
              nothing. */}
          {result.errorSheet && (
            <Button size="md" onClick={handleDownload}>
              Download error excel
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-wrap items-stretch gap-4">
        <StatCard label="Total rows" value={result.totalRows} tone="plain" />
        <StatCard label="Success" value={result.succeeded} tone="success" />
        <StatCard label="Error" value={result.failed} tone="error" />
      </div>

      {/* The grid scrolls inside its own box rather than growing the panel: a
          sheet with 200 bad rows in it must not push the footer — and the
          instruction in it — off the bottom of the screen. */}
      <div className="mt-5 max-h-[360px] overflow-auto rounded-t-[4px] border border-[#E9EDF1] custom-scrollbar-overlay">
        {/* `zoom` rather than a transform: this is a viewer, and a transform
            would scale the box along with the text and leave the scroll region
            measuring the wrong thing. */}
        <div style={{ zoom: `${zoom}%` }}>
          <table className="w-full min-w-[720px] table-fixed border-collapse text-left">
            <colgroup>
              <col className="w-[44px]" />
              <col className="w-[240px]" />
              <col />
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#F5F7F8]">
                {/* The corner cell a spreadsheet has. Empty of words, and
                    hidden from screen readers, which have the row numbers
                    themselves marked up as headers below. */}
                <th
                  scope="col"
                  aria-hidden="true"
                  className="relative border-b border-r border-[#E1E6E9] bg-[#F5F7F8] p-0"
                >
                  <span className="absolute bottom-[3px] right-[3px] block h-0 w-0 border-b-[8px] border-l-[8px] border-b-[#B6BEC3] border-l-transparent" />
                </th>
                <th
                  scope="col"
                  className="border-b border-r border-[#E1E6E9] bg-[#F5F7F8] px-3 py-2 text-center text-[13px] font-medium text-ink-muted"
                >
                  Dish name
                </th>
                <th
                  scope="col"
                  className="border-b border-[#E1E6E9] bg-[#F5F7F8] px-3 py-2 text-center text-[13px] font-medium text-ink-muted"
                >
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody>
              {result.problems.map((problem, index) => (
                // The sheet row is one per line and so unique, but a key that
                // silently collides would draw the wrong remark against a
                // name — the index is what makes that impossible.
                <tr key={`${problem.row}-${index}`}>
                  {/* The row's number in the reader's own workbook, not its
                      place in this list: it is what they scroll to. Marked up
                      as the row's header, which is what it is. */}
                  <th
                    scope="row"
                    className="border-b border-r border-[#E1E6E9] bg-[#F5F7F8] px-1 py-2 text-center text-[12px] font-normal text-ink-subtle tabular-nums align-top"
                  >
                    {problem.row}
                  </th>
                  <td className="border-b border-r border-[#E9EDF1] px-3 py-2 text-[13px] text-ink align-top">
                    {/* Blank where the name cell was the empty one — which is
                        itself one of the things a row gets refused over, and
                        the remark beside it will say so. */}
                    <span className="block truncate" title={problem.name}>
                      {problem.name}
                    </span>
                  </td>
                  <td className="border-b border-[#E9EDF1] px-3 py-2 text-[13px] text-ink align-top">
                    {problem.reason}
                  </td>
                </tr>
              ))}

              {result.problems.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="border-b border-[#E9EDF1] px-3 py-10 text-center text-[13px] text-ink-muted"
                  >
                    Every row went in.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* The status bar a spreadsheet has, and the same two things on it. */}
      <div className="flex items-center gap-3 rounded-b-[4px] border border-t-0 border-[#E9EDF1] bg-[#F5F7F8] px-2 py-1.5">
        <select
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          aria-label="Zoom the error sheet"
          className="rounded border border-[#E1E6E9] bg-white px-2 py-1 text-[12px] text-ink focus:outline-none focus:border-brand"
        >
          {ZOOM_LEVELS.map((level) => (
            <option key={level} value={level}>
              {level}%
            </option>
          ))}
        </select>

        {/* One tab, because the answer carries one list. The template has a
            Measurements sheet beside its Dish sheet, but nothing on a refused
            row says which of the two it came from — so a second tab here would
            be a label over rows that were never sorted into it. It becomes two
            the day an error carries its sheet name. */}
        <span className="rounded-t border-b-2 border-[#00C2CB] bg-white px-4 py-1 text-[13px] font-medium text-ink">
          Dish
        </span>
      </div>
    </Modal>
  );
};

export default DishImportReportModal;
